import { FormControl, Stack } from '@mui/material'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { BoxForm } from 'components/Form'
import Loading from 'components/Loading'
import Paper from 'components/Paper'
import { useGetWorkspaceOptions } from '../hooks/useGetWorkspaceOptions'
import { UserWorkspaceForForm } from '../types'
import { AddFormContainer } from './AddFormContainer'
import { NextFormContext, NextFormContextType } from './contexts/NextFormContext'
import { UserWorkspacesContext, UserWorkspacesContextType } from './contexts/UserWorkspacesContext'
import { EditFormContainer } from './EditFormContainer'
import { UserEditPublisherFormProps, UserWorkspaceForFormSlice } from './types'

export const UserEditPublisherForm = ({ user }: UserEditPublisherFormProps) => {
  const { t } = useTranslation('features/user')
  const [userWorkspaces, setUserWorkspaces] = useState<UserWorkspaceForForm[]>(user.userWorkspaces)
  const [showNextInputs, setShowNextInputs] = useState(false)

  const { loading: loadingWorkspaceOptions, workspaceOptions } = useGetWorkspaceOptions()

  const append = (userWorkspace: UserWorkspaceForForm) => {
    setUserWorkspaces([...userWorkspaces, userWorkspace])
  }

  const remove = (workspaceId: UserWorkspaceForFormSlice['workspaceId']) => {
    setUserWorkspaces(
      userWorkspaces.filter(
        (selectedUserWorkspace: UserWorkspaceForForm): boolean =>
          selectedUserWorkspace.workspaceId !== workspaceId,
      ),
    )
  }

  const update = (updatedUserWorkspace: UserWorkspaceForFormSlice) => {
    const userWorkspace: undefined | UserWorkspaceForFormSlice = userWorkspaces.find(
      (userWorkspaceTemp: UserWorkspaceForFormSlice) =>
        userWorkspaceTemp.workspaceId === updatedUserWorkspace.workspaceId,
    )

    if (userWorkspace) {
      userWorkspace.role = updatedUserWorkspace.role

      setUserWorkspaces(userWorkspaces)
    }
  }

  const userWorkspaceContextProps: UserWorkspacesContextType = {
    append,
    remove,
    update,
    userWorkspaces,
  }

  const addFormContextProps: NextFormContextType = {
    setShowNextInputs,
    showNextInputs,
  }

  if (loadingWorkspaceOptions || workspaceOptions.length === 0) {
    return <Loading />
  }

  return (
    <UserWorkspacesContext.Provider value={userWorkspaceContextProps}>
      <Paper>
        <BoxForm>
          <Stack
            direction='row'
            sx={{ gap: 2 }}
          >
            <FormControl>{t('form.publisher.common.workspaceId.label')}</FormControl>
            <FormControl>{t('form.publisher.common.role.label')}</FormControl>
          </Stack>

          {userWorkspaces.map(
            (userWorkspace: UserWorkspaceForForm, index: number): React.JSX.Element => (
              <EditFormContainer
                id={index}
                key={`EditFormContainer-${userWorkspace.workspaceId}`}
                userWorkspace={userWorkspace}
                workspaceOptions={workspaceOptions}
              />
            ),
          )}

          <NextFormContext.Provider value={addFormContextProps}>
            <AddFormContainer
              user={user}
              workspaceOptions={workspaceOptions}
            />
          </NextFormContext.Provider>
        </BoxForm>
      </Paper>
    </UserWorkspacesContext.Provider>
  )
}
